import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Typography } from "@mui/material";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { AstradaConnection } from "./types";
import CardNetworkMark from "./CardNetworkMark";
import ConnectionStatusBadge from "./ConnectionStatusBadge";
import AccountStatus from "enums/AccountStatus";
import { getImpersonationFromStorage } from "contexts/ImpersonationContext";
import AccountType from "enums/AccountType";

export interface ConnectionCardProps {
  connection: AstradaConnection;
  onManage: (connectionId: string) => void;
  onActive: (connection: AstradaConnection) => void;
}

/** One enrolled card program. See README.md. */
const ConnectionCard: React.FC<ConnectionCardProps> = ({
  connection,
  onManage,
  onActive,
}) => {
  const { t } = useTranslation();

  const impersonation = getImpersonationFromStorage();
  const isImpersonating = Boolean(impersonation);

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        padding: "var(--space-5)",
        gap: "var(--space-4)",
        backgroundColor: "var(--color-bg-elevated)",
      }}
    >
      <Inline gap="var(--space-3)" align="start" justify="between" wrap={false}>
        {/* minWidth 0 down to the title lets a long name truncate beside the badge. */}
        <Inline
          gap="var(--space-3)"
          align="center"
          wrap={false}
          sx={{ minWidth: 0 }}
        >
          <CardNetworkMark
            name={connection.astradaCard?.network || connection.name}
            size="md"
          />
          <Stack gap="var(--space-1)" sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap title={connection.name}>
              {connection.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("External card connection")}
            </Typography>
          </Stack>
        </Inline>
        <ConnectionStatusBadge status={connection.status} />
      </Inline>

      <Inline gap="var(--space-3)" align="center" justify="between">
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t(
            connection.cardCount === 1 ? "{{count}} card" : "{{count}} cards",
            {
              count: connection.cardCount,
            },
          )}
        </Typography>
        {isImpersonating &&
        connection.accountType === AccountType.ASTRADA_CONNECTION &&
        connection.status === AccountStatus.REQUESTED ? (
          <Button
            variant="default-outlined"
            onClick={() => onActive(connection)}
          >
            {t("Active")}
          </Button>
        ) : (
          <Button
            variant="default-outlined"
            onClick={() => onManage(connection.id)}
          >
            {t("Manage")}
          </Button>
        )}
      </Inline>
    </Card>
  );
};

export default ConnectionCard;
